/**
 * The coverage screen's unserved-demand join (docs/05-admin/07 §3.4 item 3): the recruiting list,
 * with the demand that makes it urgent.
 *
 * **Built from the spec, not a design** (owner ruling 2026-10-26). Two reads, joined honestly:
 *
 * - **Pujas nobody can serve**, pinned first and ordered by how many launched cities lack them.
 *   The coverage grid's zero cells are the matcher's own answer, so this list claims no supply that
 *   dispatch would refuse.
 * - **Searches that found nothing**, from the search log (`/v1/admin/search/zero-results`).
 *   ⛔ A search that found nothing names no puja (that is why it found nothing), so it cannot be
 *   joined to a puja row. It is shown beside the list with its city, not merged into it. City
 *   waitlists are per city, not per puja, and live on the geography screen.
 *
 * The search log is read-scoped to catalogue, growth and support. A supply operator without one of
 * those scopes sees which scope the panel needs, not an error.
 */
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { can, describeNeed, type Scope } from '../lib/rbac';
import { cellIndex, type CoverageGrid } from '../lib/api/hooks/supply';
import { useZeroResults } from '../lib/api/hooks/search';

export const SEARCH_LOG_SCOPES: readonly Scope[] = ['catalogue_content', 'growth', 'support'];
export const DEMAND_DAYS = 30;
const SHOWN = 10;

export interface ZeroCover {
  pujaId: number;
  puja: string;
  cities: string[];
}

/** Pujas with at least one launched city where nobody can be sent, the widest gaps first. */
export function zeroCover(grid: CoverageGrid): ZeroCover[] {
  const counts = cellIndex(grid);
  return grid.pujas
    .map((puja) => ({
      pujaId: puja.id,
      puja: puja.name,
      cities: grid.cities.filter((city) => (counts.get(`${puja.id}:${city.id}`) ?? 0) === 0).map((c) => c.name),
    }))
    .filter((row) => row.cities.length > 0)
    .sort((a, b) => b.cities.length - a.cities.length || a.puja.localeCompare(b.puja));
}

function FailedSearches({ grid }: { grid: CoverageGrid }) {
  const searches = useZeroResults(DEMAND_DAYS);
  const cityName = (id: number | null | undefined) =>
    id === null || id === undefined ? 'no city chosen' : (grid.cities.find((c) => c.id === id)?.name ?? `city #${id}`);

  if (searches.isPending) return <LoadingState rows={3} message="Reading failed searches" />;
  if (searches.isError) {
    return <ErrorState inline title="Failed searches are unavailable" cause={errorMessage(searches.error)} />;
  }
  if (searches.data.items.length === 0) {
    return <EmptyState inline icon="search" title="No failed searches" body={`Every search in the last ${DEMAND_DAYS} days found something.`} />;
  }
  return (
    <DataTable
      density="compact"
      columns={[
        { key: 'query', label: 'Searched for' },
        { key: 'city', label: 'City' },
        { key: 'times', label: 'Times', align: 'right', mono: true },
      ]}
      rows={searches.data.items.slice(0, SHOWN).map((row) => ({
        id: row.query_norm,
        query: row.query_norm,
        city: cityName(row.city_id),
        times: String(row.occurrences),
      }))}
    />
  );
}

export function UnservedDemand({ grid }: { grid: CoverageGrid }) {
  const { scope } = useConsole();
  const gaps = zeroCover(grid);

  return (
    <>
      <Section title="Pujas nobody can serve" note="The recruiting list — widest gaps first" pad={false}>
        {gaps.length === 0 ? (
          <EmptyState inline icon="check" title="Every puja has cover" body="Each active puja has a dispatchable pandit in every launched city." />
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'puja', label: 'Puja' },
              { key: 'cities', label: 'Nobody to send in' },
              { key: 'count', label: 'Cities', align: 'right', mono: true },
            ]}
            rows={gaps.map((g) => ({ id: g.pujaId, puja: g.puja, cities: g.cities.join(', '), count: String(g.cities.length) }))}
          />
        )}
      </Section>
      <Section
        title="Searches that found nothing"
        note={`Last ${DEMAND_DAYS} days, most frequent first — a failed search names no puja, so it sits beside the list`}
        pad={false}
      >
        {can(scope, SEARCH_LOG_SCOPES) ? (
          <FailedSearches grid={grid} />
        ) : (
          <EmptyState inline icon="lock" title={`Requires scope: ${describeNeed(SEARCH_LOG_SCOPES)}`} body="The search log is read by catalogue, growth and support." />
        )}
      </Section>
    </>
  );
}
